import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserRole } from '@klaro7/shared-types';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useSharedSocket } from '@/features/calls/context/CallContext';
import { getUnreadCount } from '../services/support.service';

export const SUPPORT_UNREAD_KEY = ['support-unread-count'] as const;

/**
 * Total unread support messages (staff side), for the sidebar badge — super-admin
 * only. Lives on the app-wide CallProvider socket (super-admins auto-join the
 * `staff:support` room), so the count updates in real time even when the admin
 * is not on the Supporto page.
 */
export function useSupportUnreadBadge(): number {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const qc = useQueryClient();
  const socket = useSharedSocket();

  const { data } = useQuery({
    queryKey: SUPPORT_UNREAD_KEY,
    queryFn: getUnreadCount,
    enabled: isSuperAdmin,
    staleTime: 1000 * 30,
  });

  useEffect(() => {
    if (!socket || !isSuperAdmin) return;
    // `support:unread` is emitted to the staff room both when a pro sends (count up)
    // and when any admin reads (count→0), so it alone keeps the badge live.
    const refresh = (): void => {
      qc.invalidateQueries({ queryKey: SUPPORT_UNREAD_KEY });
    };
    socket.on('support:unread', refresh);
    return () => {
      socket.off('support:unread', refresh);
    };
  }, [socket, isSuperAdmin, qc]);

  return isSuperAdmin ? (data ?? 0) : 0;
}
